'use client';

import { FC, useState } from 'react';
import { Box, Divider } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { FormTextField, FormDatePicker } from '@/components/common/FormComponents';
import { StandardDialog } from '@/components/common/StandardDialog';
import { useBackendStore } from '@/stores/backendStore';
import { useSnackbar } from '@/hooks/useSnackbar';
import {
  ProjectDto,
  ProjectActivityDto,
  VisibilityDto,
  ActivityTypeDto,
  ActivityCategoryDto,
  CreateProjectActivityRequest,
} from '@/api/ApiClient';
import { log } from '@/lib/utils/logger';

import { getActivityTypeDisplay } from '@/lib/types/activityTypes';
import { useUserInteraction } from '@/hooks/useUnsavedChanges';
import { validateRequiredToSave } from '@/lib/utils/saveValidation';
import { createProjectActivitySaveValidation } from '@/lib/validation/saveValidationBuilders';
import { useAuthStore } from '@/stores/authStore';
import { getProblemDetail } from '@/lib/utils/apiErrors';

interface AddProjectActivityDialogProps {
  open: boolean;
  onClose: () => void;
  project: ProjectDto;
  activityType?: string; // New prop for activity type
  onSubmit?: (activity: ProjectActivityDto) => void;
}

const AddProjectActivityDialog: FC<AddProjectActivityDialogProps> = ({
  open,
  onClose,
  project,
  activityType,
  onSubmit,
}) => {
  const backend = useBackendStore((state) => state.getBackend());
  const { showSnackbar } = useSnackbar();
  const currentUserId = useAuthStore((state) => state.currentUser?.id);
  const { hasUnsavedChanges, markAsInteracted } = useUserInteraction();

  // Form state
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [title, setTitle] = useState<string>('');
  const [titleError, setTitleError] = useState(false);

  // Reset form to defaults when dialog closes
  const handleClose = () => {
    setSelectedDate(new Date());
    setTitle('');
    setTitleError(false);
    onClose();
  };

  // Get display name for dialog title
  const activityTypeLabel = activityType ? getActivityTypeDisplay(activityType) || activityType : '';

  const handleSubmit = async () => {
    const saveValidation = validateRequiredToSave(
      createProjectActivitySaveValidation({
        desc: title,
        userId: currentUserId,
        date: selectedDate ?? undefined,
      })
    );
    setTitleError(!!saveValidation.fieldErrors.desc);
    if (!saveValidation.isValid) {
      showSnackbar('Please fill out all required fields.', 'error');
      return;
    }

    try {
      const payload: CreateProjectActivityRequest = {
        projectId: project.id!,
        type: (activityType || ActivityTypeDto.Proposal) as ActivityTypeDto,
        category: ActivityCategoryDto.Project,
        desc: title.trim(),
        date: selectedDate ?? new Date(),
        visibility: VisibilityDto.OnPublication,
      };

      const savedActivity = await backend.projectActivity.create(payload);
      log.debug('[AddProjectActivityDialog] Saved project activity:', savedActivity);

      onSubmit?.(savedActivity);

      showSnackbar('Project activity created successfully.', 'success');
      handleClose();
    } catch (error: unknown) {
      showSnackbar(getProblemDetail(error) ?? 'Failed to create project activity.', 'error');
    }
  };

  // Dynamic dialog title based on activity type
  const dialogTitle = activityType ? `Add ${activityTypeLabel}` : 'Add Project Activity';
  const submitText = activityType ? `Add ${activityTypeLabel}` : 'Add Project Activity';

  return (
    <StandardDialog
      open={open}
      onClose={handleClose}
      title={dialogTitle}
      maxWidth="md"
      onSubmit={handleSubmit}
      submitText={submitText}
      hasUnsavedChanges={hasUnsavedChanges}
      unsavedChangesMessage="You have unsaved changes. Are you sure you want to discard them?"
    >
      {/* Date */}
      <Box display="flex" gap={2}>
        <Box flex={1}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <FormDatePicker
              label="Date"
              value={selectedDate}
              onChange={(newValue) => {
                setSelectedDate(newValue);
                markAsInteracted();
              }}
              required
            />
          </LocalizationProvider>
        </Box>
      </Box>

      <Divider />

      {/* Description */}
      <FormTextField
        label="Description"
        required
        value={title}
        onChange={(e) => {
          setTitle(e.target.value);
          markAsInteracted();
        }}
        size="small"
        error={titleError}
        fullWidth
      />
    </StandardDialog>
  );
};

export { AddProjectActivityDialog };
